/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { DEFAULT_APPLICATION_NAME, DEFAULT_DEVICE_TYPE, DEFAULT_MSO_NAME } from '../../config';
import { Log } from '../logging/Log';
import { Storage } from '../storage/Storage';
import { MessageService } from './MessageService';

export class TivoSettings {
    private oTivoSettings: any = {};

    private static TAG = 'TivoSettings';

    private async fetchAppConfig(resolve: Function, reject: Function) {
        try {
            const configFileName = `${this.getMsoName() || DEFAULT_MSO_NAME}-config.json`;
            const configFileURL = `https://d2sj8cz5agvefv.cloudfront.net/${configFileName}`;
            const response = await fetch(configFileURL);
            const configs = await response.json();
            let msoName: string = this.getMsoName();
            if (!msoName) {
                const [firstMsoName] = Object.keys(configs);
                msoName = firstMsoName;
            }
            const appConfiguration = configs[msoName];
            this.oTivoSettings = { ...this.oTivoSettings, ...appConfiguration };
            resolve();
        } catch (error) {
            Log.e(TivoSettings.TAG, 'Failed to get app config from config.json.');
            Log.e(TivoSettings.TAG, 'stacktrace:', error);
            reject(error);
        }
    }

    initialize = async () => {
        return new Promise((resolve, reject) => {
            new MessageService(MessageService.GET_TIVO_SETTINGS)
                .getResponse()
                .then((response) => {
                    Log.i(TivoSettings.TAG, 'Got the TiVo Settings', response);
                    this.oTivoSettings = response === true ? {} : <JSON>response;
                })
                .catch((error) => {
                    Log.e(TivoSettings.TAG, 'Failed to initialize TiVo Settings.', error);
                })
                .finally(() => {
                    this.fetchAppConfig(resolve, reject);
                });
        });
    };

    getAppName = (): string => {
        // TODO: replace app name from branding ui bundle
        return `${this.getMsoName()}-${this.getDeviceType()}`;
    };

    getDeviceType = () => {
        return (this.oTivoSettings && this.oTivoSettings.deviceType) || DEFAULT_DEVICE_TYPE;
    };

    getDisplayName = () => {
        return (this.oTivoSettings && this.oTivoSettings.appName) || DEFAULT_APPLICATION_NAME;
    };

    getMsoName = () => {
        return this.oTivoSettings && this.oTivoSettings.msoName;
    };

    getMsoPartnerId = () => {
        return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
            Storage.Keys.BODY_INFO_SERVICE_DATA,
        ).msoPartnerId;
    };

    getPartnerCustomerId = () => {
        return (
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItem(
                Storage.Keys.BODY_INFO_SERVICE_DATA,
            )?.partnerCustomerId || ''
        );
    };

    getProvisioningDomain() {
        return this.oTivoSettings && this.oTivoSettings.provisioningDomain;
    }

    getSlsBaseUrl() {
        return this.oTivoSettings?.slsEndpointsUrl;
    }

    getSlsDomain = () => {
        return this.oTivoSettings && this.oTivoSettings.slsDomain;
    };

    getShellVersion = () => {
        return (this.oTivoSettings && this.oTivoSettings.shellVersion) || '';
    };

    getWebAppUrl = () => {
        return (this.oTivoSettings && this.oTivoSettings.webAppUrl) || '';
    };

    getBodyId = () => {
        return this.oTivoSettings.bodyId;
    };

    /**
     *
     * @returns {string} tsn number from bodyId
     */
    getDeviceTsn() {
        const bodyId = this.getBodyId();
        return bodyId ? bodyId.replace('tsn:', '') : '';
    }

    getSegmentTransportKey() {
        return this.oTivoSettings && this.oTivoSettings.segmentKey;
    }

    /**
     * @returns {object} Player MSO config.
     */
    getPlayerMsoConfig() {
        return (this.oTivoSettings && this.oTivoSettings.msoOverride?.playerMsoConfig) || {};
    }

    /**
     * @returns {boolean} 608 Closed Caption MSO config.
     */
    isClosedCaption608Allowed() {
        return this.oTivoSettings?.msoOverride?.closedCaption608Allowed ?? false;
    }

    /**
     * @returns {boolean} 708 Closed Caption MSO config.
     */
    isClosedCaption708Allowed() {
        return this.oTivoSettings?.msoOverride?.closedCaption708Allowed ?? false;
    }

    /**
     * @returns {boolean} Smpte Subtitle MSO config.
     */
    isSubtitleSmpteAllowed() {
        return this.oTivoSettings?.msoOverride?.subtitleSmpteAllowed ?? true;
    }

    /**
     * check config.json for key browserAllowed to allow app to execute in browser without shell app
     * @returns app allowed to execute in browser without shell app;
     */
    isBrowserAllowed = (): boolean => {
        return !!this.oTivoSettings?.browserAllowed;
    };

    setBodyId(bodyId: string) {
        this.oTivoSettings.bodyId = bodyId;
    }

    setProvisioningDomain(domain: string) {
        this.oTivoSettings.provisioningDomain = domain;
    }
}
